import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnChanges, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CartService } from '../../providers/cart/cart.service';
import { Asset } from '../../../common/generated-types';
import { ButtonData, HeroSectionData, KeySellingPointData, MarketingBannerData, ExternalHtmlContentData, TitleInfo } from '../../../common/interfaces';
import { SwiperContainer } from 'swiper/element';
import Swiper from 'swiper';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { StateService } from '../../providers/state/state.service';
import { take } from 'rxjs/operators';
import { ButtonActionType, RoundedCornerSize, TextAlignment } from '../../../common/enums';
import { getRoundedClass } from '../../../common/utils/styles-helper';
import { navigateByNavigationItem, resolveButtonNavigationItem } from '../../../common/utils/navigation-helpers';

@Component({
    selector: 'vsf-marketing-banner',
    templateUrl: './marketing-banner.component.html',
    styleUrls: ['./marketing-banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingBannerComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() marketingBanner: MarketingBannerData;
    @Input() linePadding: string;
    @Input() sectionRoundedClass: string = '';
    @Input() slideEnabled: boolean = true;
    @Input() isMobile: boolean | null;
    @Input() titleInfo?: TitleInfo;
    @Input() pageFullWidth: boolean = false;
    @Input() sectionMaxHeight: string = '';

    @ViewChild('leftMediaDesktopSwiper') leftMediaDesktopSwiper: ElementRef<SwiperContainer>;
    @ViewChild('rightMediaDesktopSwiper') rightMediaDesktopSwiper: ElementRef<SwiperContainer>;
    @ViewChild('belowMediaMultipleSwiper') belowMediaMultipleSwiper: ElementRef<SwiperContainer>;
    @ViewChild('belowMediaSingleSwiper') belowMediaSingleSwiper: ElementRef<SwiperContainer>;
    @ViewChild('mobileMediaSwiper') mobileMediaSwiper: ElementRef<SwiperContainer>;
    @ViewChild('mediaOnlySwiper') mediaOnlySwiper: ElementRef<SwiperContainer>;
    @ViewChild('titledMediaSwiper') titledMediaSwiper: ElementRef<SwiperContainer>;
    @ViewChild('rightSlideSwiper') rightSlideSwiper: ElementRef<SwiperContainer>;
    @ViewChild('leftSlideSwiper') leftSlideSwiper: ElementRef<SwiperContainer>;
    @ViewChild('mobileSlideSwiper') mobileSlideSwiper: ElementRef<SwiperContainer>;
    private swiperInstance: Swiper;

    constructor(@Inject(PLATFORM_ID) private platformId: object,
                private cartService: CartService,
                private stateService: StateService,
                private changeDetector: ChangeDetectorRef,
                private router: Router,
                private logger: NGXLogger,
                private el: ElementRef) {
                    this.isBrowser = isPlatformBrowser(this.platformId);
                }

    type: string;
    isPageHeading: boolean = false;
    h1StyleMode: string = 'auto';
    medias: Asset[] = [];
    mobileMedias: Asset[] = [];
    mobileMediaPosition: string;
    mobileMediaStyle: string;
    bannerMedias: Asset[] = [];
    mediaHeight?: number;
    mediaRoundedCorners: RoundedCornerSize = RoundedCornerSize.None;
    title: string;
    text: string;
    titleFontSize: number = 30;
    textFontSize: number = 16;
    mobileTitleFontSize: number = 20;
    mobileTextFontSize: number = 14;
    textColor: string;
    textAlignment: string;
    textWidth: string;
    textIndentation: string;
    titleIndentation: string;
    leftRightPartRatio: number;
    mediaTextGap: number = 0;
    shadowTransparencyValue: number;
    backgroundMedia: Asset | null;
    backgroundMobileMedia: Asset | null;
    autoSlideDelay: number = 3.0;
    layoutStyle: string = 'normal';
    backgroundColor: string;
    button: ButtonData | null;
    buttonPosition: string;
    buttonAlignment: TextAlignment = TextAlignment.Center;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
    keyPointsLayoutType: string;
    mobileKeyPointsLayoutType: string;
    keyPoints: KeySellingPointData[] = [];
    heroSection: HeroSectionData;
    externalHtmlContent: ExternalHtmlContentData;
    faqList: string;
    slidesPerView: number;
    inFlight: boolean = false;
    baseUrl = '';
    mediaContainerWidth: number;
    textContainerWidth: number;
    private subscription = new Subscription();
    backgroundMediaHeight = 400;
    backgroundMediaWidth = 0;
    backgroundMobileMediaHeight = 0;
    backgroundMobileMediaWidth = 400;
    mobileImageSize = 'large';
    mediasHeight = 400;
    mediasWidth = 540;
    mediaTextGapInPx: string | null = null;
    isBrowser: boolean;

    get isComponentTitle(): boolean {
        return (this.titleInfo?.titleMode === 'for-component' && this.titleInfo?.title?.length > 0);
    }

    ngOnInit(): void {
        this.stateService.select(state => state.storePath)
        .pipe(take(1)).subscribe(baseUrl => {
            this.baseUrl = baseUrl;
        });
        const marketingBanner = this.marketingBanner;
        if(marketingBanner) {
            this.type = marketingBanner.type;
            this.medias = marketingBanner.medias;
            this.mobileMedias = marketingBanner.mobileMedias;
            this.mobileMediaPosition = marketingBanner.mobileMediaPosition;
            this.mobileMediaStyle = marketingBanner.mobileMediaStyle;
            this.mediaHeight = marketingBanner.mediaHeight || 400;
            this.mediaRoundedCorners = (marketingBanner.mediaRoundedCorners ?? 'none') as any;
            this.title = marketingBanner.title;
            this.isPageHeading = this.titleInfo?.isPageHeading === true;
            this.h1StyleMode = this.titleInfo?.h1StyleMode || 'auto';
            this.text = marketingBanner.text;
            this.titleFontSize = marketingBanner.titleFontSize ?? this.titleFontSize;
            this.textFontSize = marketingBanner.textFontSize ?? this.textFontSize;
            this.mobileTitleFontSize = marketingBanner.titleFontSize ? Math.round(marketingBanner.titleFontSize * 2 / 3) : this.mobileTitleFontSize;
            this.mobileTextFontSize = marketingBanner.textFontSize ? (marketingBanner.textFontSize - 2) : this.mobileTextFontSize;
            this.textColor = marketingBanner.textColor;
            this.textAlignment = marketingBanner.textAlignment;
            this.textWidth = marketingBanner.textWidth*100 + '%';
            this.textIndentation = marketingBanner.textIndentation + 'px';
            this.titleIndentation = marketingBanner.titleIndentation + 'px';
            this.leftRightPartRatio = marketingBanner.leftRightPartRatio || 0.5;
            this.mediaTextGap = marketingBanner.mediaTextGap || 0;
            this.mediaTextGapInPx = this.mediaTextGap ? this.mediaTextGap + 'px' : null;
            this.shadowTransparencyValue = marketingBanner.shadowTransparencyValue;
            this.autoSlideDelay = marketingBanner.autoSlideDelay;
            this.layoutStyle = marketingBanner.layoutStyle;
            this.backgroundMedia = marketingBanner.backgroundMedia;
            this.backgroundMobileMedia = marketingBanner.backgroundMobileMedia;
            this.backgroundColor = marketingBanner.backgroundColor;
            this.button = marketingBanner.button;
            this.buttonPosition = this.isMobile ? marketingBanner.buttonPositionMobile: marketingBanner.buttonPosition ;
            this.buttonAlignment = (this.isMobile ? marketingBanner.buttonAlignmentMobile : marketingBanner.buttonAlignment) || TextAlignment.Center;
            this.buttonLocationX = marketingBanner.buttonLocationX;
            this.buttonLocationY = marketingBanner.buttonLocationY;
            this.buttonLocationXMobile = marketingBanner.buttonLocationXMobile;
            this.buttonLocationYMobile = marketingBanner.buttonLocationYMobile;
            this.bannerMedias = (this.isMobile && this.mobileMedias?.length > 0) ? this.mobileMedias : this.medias;
            this.slidesPerView = Math.min(this.medias.length, 3);
            if(this.type === 'titled-media' || this.type === 'media-only' || this.type === 'top-text-below-media') {
                this.mobileImageSize = 'full';
            }
        }
    }

    ngOnChanges() {
        if(this.swiperInstance) {
            this.swiperInstance.update();
            this.changeDetector.detectChanges();
        }
    }

    ngAfterViewInit() {
        if(!this.isBrowser) {
            return;
        }
        if(this.type === 'left-media-right-text') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileMediaSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                this.swiperInstance = this.leftMediaDesktopSwiper?.nativeElement.swiper;
            }
        } else if(this.type === 'left-text-right-media') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileMediaSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                this.swiperInstance = this.rightMediaDesktopSwiper?.nativeElement.swiper;
            }
        }  else if(this.type === 'top-text-below-media') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileMediaSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                if(this.bannerMedias.length > 3) {
                    this.swiperInstance = this.belowMediaMultipleSwiper?.nativeElement.swiper;
                } else {
                    this.swiperInstance = this.belowMediaSingleSwiper?.nativeElement.swiper;
                }
            }
        } else if(this.type === 'media-only') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileMediaSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                this.swiperInstance = this.mediaOnlySwiper?.nativeElement.swiper;
            }
        } else if(this.type === 'titled-media') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileMediaSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                this.swiperInstance = this.titledMediaSwiper?.nativeElement.swiper;
            }
        } else if(this.type === 'left-media-slide-with-background' || this.type === 'right-media-slide-with-background') {
            if(this.isMobile) {
                this.swiperInstance = this.mobileSlideSwiper?.nativeElement.swiper;
            } else if(this.isMobile === false) {
                this.swiperInstance = this.type === 'left-media-slide-with-background' ?
                    this.leftSlideSwiper?.nativeElement.swiper :
                    this.rightSlideSwiper?.nativeElement.swiper;
            }
        }
        if(this.swiperInstance && this.autoSlideDelay < 100) {
            this.swiperInstance.params.autoplay = {
                delay: this.autoSlideDelay * 1000,
                disableOnInteraction: false,
            };
            this.swiperInstance.autoplay.start();
        }
        this.changeDetector.detectChanges();
        if (this.hasMediaContainer) {
            setTimeout(() => this.calculateSwiperSize());
        }
    }

    calculateWidths() {
        const totalRatio = 1.0;
        let textWidthPercentage = 50;
        let mediaWidthPercentage = 50;
        if (this.type === 'left-media-right-text' || this.type ==='left-media-slide-with-background') {
            mediaWidthPercentage = this.leftRightPartRatio * 100;
            textWidthPercentage = (totalRatio - this.leftRightPartRatio) * 100;
        } else if (this.type === 'left-text-right-media' || this.type ==='right-media-slide-with-background') {
            textWidthPercentage = this.leftRightPartRatio * 100;
            mediaWidthPercentage = (totalRatio - this.leftRightPartRatio) * 100;
        }
        return { text: `${textWidthPercentage}%`, media: `${mediaWidthPercentage}%` };
    }

    getMediaContainerClass(position: string): string {
        switch (position) {
            case 'top':
                return 'align-top';
            case 'bottom':
                return 'align-bottom';
            case 'center':
            default:
                return 'align-center';
        }
    }

    getButtonStyles() {
        const position = this.buttonPosition;
        const locationX = (this.isMobile ? this.buttonLocationXMobile : this.buttonLocationX);
        const locationY = (this.isMobile ? this.buttonLocationYMobile : this.buttonLocationY);
        if (position === 'custom') {
          return {
            position: 'absolute',
            left: `${locationX * 100}%`,
            top: `${locationY * 100}%`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'auto',
            zIndex: 10,
          };
        } else {
          return {};
        }
    }

    getButtonXLocationStyles() {
        const locationX = (this.isMobile ? this.buttonLocationXMobile : this.buttonLocationX);
        const leftPos = `${locationX * 100}%`;
        return {
          position: 'relative',
          left: leftPos,
          transform: 'translateX(-50%)',
        };
    }

    get roundedClass(): string {
        return getRoundedClass(this.mediaRoundedCorners);
    }

    private readonly mediaTypesWithContainer = new Set([
        'left-media-right-text',
        'left-text-right-media',
        'top-text-below-media',
        'media-only',
        'titled-media',
        'left-media-slide-with-background',
        'right-media-slide-with-background',
    ]);

    private get hasMediaContainer(): boolean {
        return !!this.type && this.mediaTypesWithContainer.has(this.type);
    }

    buttonClick() {
        this.logger.debug('Button clicked', this.button);
        if(this.button?.enabled && this.button?.actionType) {
            if(this.button.actionType === ButtonActionType.NavigateTo) {
                const navigationItem = resolveButtonNavigationItem(this.button);
                if (navigationItem) {
                    navigateByNavigationItem(this.router, navigationItem, this.baseUrl);
                }
            } else if(this.button.actionType === ButtonActionType.AddVariantToCart) {
                if(this.button.selectedVariantId) {
                    if(this.button.isVariantCustomizable) {
                        this.router.navigate(
                            [`${this.baseUrl}/dp`, this.button.targetProduct?.slug],
                            { queryParams: { variantid: this.button.selectedVariantId } }
                        );
                    } else {
                        this.addToCartAndOpenDrawer(this.button.selectedVariantId);
                    }
                }
            }
        }
    }

    addToCartAndOpenDrawer(productVariantId: string) {
        this.inFlight = true;
        this.changeDetector.markForCheck();

        this.subscription.add(
          this.cartService.addToCartAndOpenDrawer(productVariantId).subscribe({
            next: () => {
              this.inFlight = false;
              this.changeDetector.markForCheck();
            },
            error: err => {
              this.inFlight = false;
              this.logger.error('Error in addToCartAndOpenDrawer', err);
              this.changeDetector.markForCheck();
            },
          }),
        );
    }

    private calculateSwiperSize() {
        if(isPlatformServer(this.platformId)) {
            return;
        }
        const mediaContainer = this.el.nativeElement.querySelector('.media-container');
        if (!mediaContainer) {
            this.logger.warn('Media container not found');
            return { width: 540, height: 400 };
        }
        const rect = mediaContainer.getBoundingClientRect();
        if(rect) {
            if (this.isMobile) {
                this.backgroundMobileMediaHeight = rect.height;
                this.backgroundMobileMediaWidth = rect.width;
            } else {
                this.mediasHeight = rect.height;
                this.backgroundMediaHeight = rect.height;
                if(this.slidesPerView === 1) {
                    this.mediasWidth = rect.width;
                    this.backgroundMediaWidth = rect.width;
                } else {
                    this.mediasWidth = rect.width / this.slidesPerView;
                    this.backgroundMediaWidth = rect.width / this.slidesPerView;
                }
            }
        }
        this.changeDetector.detectChanges();
    }
}
